import type { FC } from "react";
import { useId, useState } from "react";

import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
} from "@ui/components/Drawer";
import { I18n } from "@ui/components/I18n";
import { useFlavorsSelectionController } from "@ui/hooks/useFlavorsSelectionController";
import { useTranslations } from "@ui/providers/useTranslations";
import classnames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import type { FlavorsSelection, StockStatusElements } from "@jti/fe-common";

import { MultipleFlavorSelectorSticksBlock } from "./components/MultipleFlavorSelectorSticksBlock";
import { MultipleFlavorSelectorToggler } from "./components/MultipleFlavorSelectorToggler";
import { MultipleFlavorsSelectorResult } from "./components/MultipleFlavorsSelectorResult";

import { type MultipleFlavorsSelectorItem } from "./MultipleFlavorsSelector.types";

import styles from "./MultipleFlavorsSelector.module.scss";

type MultipleFlavorsSelectorProps = {
  items: MultipleFlavorsSelectorItem[];
  stockStatusElements: StockStatusElements;
  onChange: (flavorSelection: FlavorsSelection) => void;
  limit: number;
  defaultFlavorsSelection?: FlavorsSelection | null;
  shouldValidate?: boolean;
  hasError?: boolean;
  isProductDataLoading?: boolean;
  error?: string;
  className?: string;
  drawerHeadline?: string;
};

export const MultipleFlavorsSelector: FC<MultipleFlavorsSelectorProps> = ({
  items,
  stockStatusElements,
  onChange,
  limit,
  defaultFlavorsSelection,
  shouldValidate = false,
  hasError = false,
  isProductDataLoading = false,
  error,
  className,
  drawerHeadline,
}) => {
  const { __ } = useTranslations();

  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  const {
    flavorSelection,
    selectedPacksCount,
    isLimitReached,
    temporarySelection,
    temporarySelectedPacksCount,
    isTemporaryLimitReached,
    hasValidationError,
    setTemporarySelection,
    onCollectionChange,
    onTemporaryCollectionChange,
    onDrawerApplySelection,
  } = useFlavorsSelectionController({
    limit,
    defaultFlavorsSelection,
    onChange,
    validate: shouldValidate,
    hasError,
  });

  const errorBlockId = useId();
  const desktopSelectorContentId = useId();
  const mobileSelectorContentId = useId();

  return (
    <div className={className}>
      <section
        className={styles.main}
        data-testid="MultipleFlavorsSelector"
        aria-label={drawerHeadline || __("productdetails.sticks.drawerHeadline")}
        aria-describedby={hasValidationError ? errorBlockId : undefined}
      >
        <div
          className={classnames(styles.contentContainer, {
            [styles.hasError]: !!hasValidationError || !!error,
          })}
        >
          <div className={styles.desktopView} data-testid="MultipleFlavorsSelector-desktopView">
            <DrawerRoot onCancelCallback={() => setIsSelectorOpen(false)}>
              <DrawerTrigger>
                <MultipleFlavorSelectorToggler
                  onClick={() => {
                    setTemporarySelection(flavorSelection);
                    setIsSelectorOpen(true);
                  }}
                  selectedItemsCount={selectedPacksCount}
                  itemsLimit={limit}
                  availableFlavorsCount={items.length}
                  isSelectorOpen={isSelectorOpen}
                  controlledElementId={desktopSelectorContentId}
                />
              </DrawerTrigger>

              <MultipleFlavorsSelectorResult
                className={classnames(styles.selectionResult, {
                  [styles.hidden]: selectedPacksCount === 0,
                })}
                userSelection={flavorSelection}
                items={items}
              />

              <DrawerContent aria-label={__("productdetails.sticks.drawerHeadline")} id={desktopSelectorContentId}>
                <DrawerHeader>
                  {drawerHeadline || __("productdetails.sticks.drawerHeadline")}
                  <div className={styles.drawerSubheader} aria-live="polite">
                    <I18n t="productdetails.sticks.selection">
                      <strong>{temporarySelectedPacksCount}</strong>
                      {limit}
                    </I18n>
                  </div>
                </DrawerHeader>

                <DrawerBody>
                  <ul className={styles.sticksList}>
                    {items.map((item) => {
                      return (
                        <li key={item.id}>
                          <MultipleFlavorSelectorSticksBlock
                            label={item.name ?? ""}
                            description={item?.description ?? ""}
                            id={item.id}
                            imageUrl={item.image.url ?? ""}
                            stockStatus={stockStatusElements[item.id]}
                            initialQuantity={temporarySelection[item.id] || 0}
                            isLimitReached={isTemporaryLimitReached}
                            onChange={onTemporaryCollectionChange}
                            isStockStatusLoading={isProductDataLoading}
                            attributes={item.attributes ?? []}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </DrawerBody>

                <DrawerFooter>
                  <DrawerCloseTrigger>
                    <Button
                      onClick={() => onDrawerApplySelection(() => setIsSelectorOpen(false))}
                      variant="primary"
                      data-testid="applySelectionButton"
                    >
                      {__("productdetails.sticks.applySelection")}
                    </Button>
                  </DrawerCloseTrigger>
                </DrawerFooter>
              </DrawerContent>

              <DrawerBackdrop aria-label={__("common.close")} />
            </DrawerRoot>
          </div>

          <div className={styles.mobileView} data-testid="MultipleFlavorsSelector-mobileView">
            <MultipleFlavorSelectorToggler
              onClick={() => setIsSelectorOpen(!isSelectorOpen)}
              selectedItemsCount={selectedPacksCount}
              itemsLimit={limit}
              availableFlavorsCount={items.length}
              isSelectorOpen={isSelectorOpen}
              controlledElementId={mobileSelectorContentId}
            />

            <MultipleFlavorsSelectorResult
              className={classnames(styles.selectionResult, {
                [styles.hidden]: selectedPacksCount === 0 || !isSelectorOpen,
              })}
              userSelection={flavorSelection}
              items={items}
            />

            <section
              id={mobileSelectorContentId}
              role="dialog"
              className={classnames(styles.selector, {
                [styles.active]: isSelectorOpen,
              })}
            >
              <ol className={styles.sticksList}>
                {items.map((item) => {
                  return (
                    <li key={item.id}>
                      <MultipleFlavorSelectorSticksBlock
                        label={item.name ?? ""}
                        description={item.description ?? ""}
                        id={item.id}
                        isLimitReached={isLimitReached}
                        initialQuantity={flavorSelection[item.id] || 0}
                        imageUrl={item.image.url ?? ""}
                        stockStatus={stockStatusElements[item.id]}
                        onChange={onCollectionChange}
                        isStockStatusLoading={isProductDataLoading}
                        attributes={item.attributes ?? []}
                      />
                    </li>
                  );
                })}
              </ol>
            </section>
          </div>
        </div>
      </section>

      {hasValidationError && (
        <div className={styles.error} role="alert" id={errorBlockId}>
          {__("productdetails.sticks.validationError")}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};
